import Badge, { ColorType } from '@/app/common/components/Badge'
import { ACETable } from '@/app/common/components/Table/ACETable'
import { useAceTable } from '@/app/common/components/Table/useAceTable'
import { formatDateToUsersTimezone } from '@/lib/utils'
import {
  SystemCatalogCategory,
  SystemCatalogItem,
  SystemCatalogItemStatus,
} from '@/queries/system_catalog/system_catalog.schema'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shad/ui/tooltip'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import { commandScore } from '../../utils/system-catalog.utils'
import { AddItemPopup } from '../ItemPopups/AddItemPopup'
import EditItemPopup from '../ItemPopups/EditItemPopup'
import CategoriesFilter from './CategoriesFilter'
const columnHelper = createColumnHelper<SystemCatalogItem>()

type SystemCatalogItemsListProps = {
  categories: SystemCatalogCategory[]
  items: SystemCatalogItem[]
  invalidateCategories: () => void
  invalidateItems: () => void
}

function SystemCatalogItemsTable({
  categories,
  items,
  invalidateCategories,
  invalidateItems,
}: SystemCatalogItemsListProps) {
  const aceTable = useAceTable()
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([])

  function getBadgeColor(status: SystemCatalogItemStatus): ColorType {
    if (status === 'Active') {
      return 'Positive'
    }

    if (status === 'Inactive') {
      return 'Negative'
    }

    return 'Notice'
  }

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="text-left">
              <div className="w-[7rem] max-w-[7rem] truncate">
                {info.getValue()}
              </div>
            </TooltipTrigger>
            <TooltipContent>{info.getValue()}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => (
        <Badge semanticColor={getBadgeColor(info.getValue())}>
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('type_name', {
      header: 'Type',
      cell: (info) => {
        return <Badge semanticColor="Neutral">{info.getValue()}</Badge>
      },
    }),
    columnHelper.accessor('created_at', {
      header: 'Created At',
      cell: (info) => {
        const value = info.getValue()
        return value ? formatDateToUsersTimezone(value) : '-'
      },
    }),
    columnHelper.accessor('updated_at', {
      header: 'Updated At',
      cell: (info) => {
        const value = info.getValue()
        return value ? formatDateToUsersTimezone(value) : '-'
      },
    }),
    columnHelper.accessor('id', {
      header: 'Action',
      cell: (info) => {
        const rowData = info.row.original
        return (
          <EditItemPopup
            onSuccess={() => {
              invalidateCategories()
              invalidateItems()
            }}
            categories={categories}
            item={rowData}
          />
        )
      },
    }),
  ]

  function getFilteredItems() {
    if (selectedCategoryIds.length === 0) {
      return items
    }
    const validCategories = categories.filter((category) => {
      return selectedCategoryIds.includes(category.id)
    })
    const validItemIds = validCategories.flatMap((category) => {
      return category.items.map((item) => item.id)
    })
    const filteredItems = items.filter((item) => {
      return validItemIds.includes(item.id)
    })

    return filteredItems
  }

  const selectedItems =
    aceTable.search.length === 0
      ? getFilteredItems()
      : getFilteredItems()
          .map((item) => ({
            item,
            score: commandScore(item.name, aceTable.search),
          }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ item }) => item)

  const { currentPage, recordsPerPage } = aceTable
  const previousPage = currentPage - 1
  const currentItems = selectedItems.slice(
    previousPage * recordsPerPage,
    currentPage * recordsPerPage
  )

  return (
    <div className="w-full h-full">
      <div className="mb-4 flex justify-end">
        <AddItemPopup
          onAddItemSuccess={() => {
            invalidateCategories()
            invalidateItems()
          }}
          categories={categories}
        />
      </div>

      <ACETable<SystemCatalogItem>
        {...aceTable}
        totalRows={selectedItems.length}
        onRefresh={() => {
          invalidateCategories()
        }}
        data={currentItems}
        title={'System Catalog Items'}
        columns={columns}
        filter={
          <CategoriesFilter
            onFilterChange={(categoryIds) => {
              aceTable.setCurrentPage(1)
              setSelectedCategoryIds(categoryIds)
            }}
            selectedCategoryIds={selectedCategoryIds}
            categories={categories}
          />
        }
      />
    </div>
  )
}

export default SystemCatalogItemsTable
